import { useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Info, Download } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { computeFSIBreakdownFromFormData } from "@/utils/calculations";
applyPlugin(jsPDF);

export const FSICalculator = ({ formData, setFormData, setFSIBreakdown }) => {
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSliderChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value[0]
    });
  };

  // Use shared FSI breakdown calculator (single source of truth)
  const fsiBreakdown = useMemo(() => computeFSIBreakdownFromFormData(formData), [formData]);


  // Pass to parent when fsiBreakdown changes
  useEffect(() => {
    if (setFSIBreakdown && fsiBreakdown) {
      setFSIBreakdown(fsiBreakdown);
    }
  }, [fsiBreakdown, setFSIBreakdown]);

  const exportFSICalcPDF = () => {
    if (!fsiBreakdown) return;
    const fb = fsiBreakdown;
    const fn = (v) => parseFloat(v || 0).toFixed(2);
    const projectName = formData.projectName || formData.societyName || "Project";

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("FSI Calculation Statement", pageWidth / 2, y, { align: "center" });
    y += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(projectName, pageWidth / 2, y, { align: "center" });
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, pageWidth / 2, y, { align: "center" });
    doc.setTextColor(0);
    y += 10;

    // Project Info
    doc.autoTable({
      startY: y,
      head: [["Project Details", "Value"]],
      body: [
        ["Plot Area (Gross)", `${fn(fb.grossPlotArea)} sq m`],
        ["Plot Area (Net)", `${fn(fb.netPlotArea)} sq m`],
        ["Road Width", `${formData.roadWidth} ${formData.roadWidthUnit}`],
        ["Project Classification", (formData.projectClassification || "non-tod").toUpperCase()],
        ["Project Type", (formData.projectType || "residential").charAt(0).toUpperCase() + (formData.projectType || "residential").slice(1)],
        ["Permissible FSI", fn(fb.permissibleFSI)],
        ...(fb.useLowerFSI ? [["Effective FSI (Lower)", fn(fb.effectiveFSI)]] : []),
      ],
      theme: "grid",
      headStyles: { fillColor: [30, 64, 175], fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Road Widening
    if (fb.totalRoadWidening > 0) {
      doc.autoTable({
        startY: y,
        head: [["Road Widening", "Value"]],
        body: [
          ["Old Road Widening", `${fn(fb.oldRoadWidening)} sq m`],
          ["New Road Widening", `${fn(fb.newRoadWidening)} sq m`],
          ["Total Road Widening", `${fn(fb.totalRoadWidening)} sq m`],
          ["TDR Reduction from Widening", `${fn(fb.tdrReductionFromRoadWidening)} sq m`],
        ],
        theme: "grid",
        headStyles: { fillColor: [100, 100, 100], fontSize: 10 },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 1: { halign: "right" } },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // FSI Breakdown
    const fsiRows = [
      ["Basic FSI (1.1)", fn(fb.basicFSI), `${fn(fb.basicFSIArea)} sq m`],
    ];
    if (fb.is6MeterRoad && fb.incentiveFSIArea > 0) {
      fsiRows.push(["Incentive FSI (0.3)", "0.30", `${fn(fb.incentiveFSIArea)} sq m`]);
    }
    if (fb.isMhada) {
      fsiRows.push([`MHADA FSI (${fn(fb.mhadaFSI)})`, fn(fb.mhadaFSI), `${fn(fb.mhadaFSIArea)} sq m`]);
      if (fb.mhadaIncentiveFSIArea > 0) {
        fsiRows.push([`Incentive FSI (26.20 x ${parseInt(formData.numberOfMembers) || 0} members)`, '', `${fn(fb.mhadaIncentiveFSIArea)} sq m`]);
      }
      if (fb.titbitArea > 0) {
        fsiRows.push([`Titbit Area`, '', `${fn(fb.titbitArea)} sq m`]);
      }
    } else {
      fsiRows.push(["Paid FSI", fn(fb.paidFSI), `${fn(fb.paidFSIArea)} sq m`]);
      fsiRows.push(["Total TDR", fn(fb.totalTDR), `${fn(fb.tdrArea)} sq m`]);
      if (fb.totalTDR > 0) {
        fsiRows.push(["  - Amenity TDR", fn(fb.amenityTDR), `${fn(fb.amenityTDR * fb.grossPlotArea)} sq m`]);
        fsiRows.push(["  - Slum TDR", fn(fb.slumTDR), `${fn(fb.slumTDR * fb.grossPlotArea)} sq m`]);
      }
      if (fb.isCbd && fb.cbdPremiumFSI > 0) {
        fsiRows.push([`CBD Premium FSI (+${fn(fb.cbdPremiumFSI)})`, fn(fb.cbdPremiumFSI), `${fn(fb.cbdPremiumArea)} sq m`]);
      }
    }
    fsiRows.push(["Green FSI", fn(fb.greenFSI), `${fn(fb.greenFSIArea)} sq m`]);

    const buildableRatio = (fb.totalBuildableArea / fb.grossPlotArea).toFixed(2);
    const totalRatio = (fb.totalFSIWithAncillary / fb.grossPlotArea).toFixed(2);

    doc.autoTable({
      startY: y,
      head: [["FSI Component", "Ratio", "Area"]],
      body: fsiRows,
      foot: [
        [`TOTAL BUILDABLE (${buildableRatio} FSI)`, "", `${fn(fb.totalBuildableArea)} sq m`],
      ],
      theme: "grid",
      headStyles: { fillColor: [30, 64, 175], fontSize: 10 },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "center" }, 2: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Ancillary
    doc.autoTable({
      startY: y,
      head: [["Ancillary FSI", "Area"]],
      body: [
        ["Residential (60%)", `${fn(fb.ancillaryResidential)} sq m`],
        ["Commercial (80%)", `${fn(fb.ancillaryCommercial)} sq m`],
        ["Total Ancillary", `${fn(fb.totalAncillary)} sq m`],
      ],
      foot: [
        [`TOTAL WITH ANCILLARY (${totalRatio} FSI)`, `${fn(fb.totalFSIWithAncillary)} sq m`],
      ],
      theme: "grid",
      headStyles: { fillColor: [22, 163, 74], fontSize: 10 },
      footStyles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 11 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });

    doc.save(`FSI_Calculation_${projectName.replace(/\s+/g, '_')}.pdf`);
    toast.success("FSI Calculation exported as PDF");
  };

  return (
    <div className="space-y-6">
      {/* Lower FSI Option */}
      {!fsiBreakdown?.is6MeterRoad && (
        <Card className="rounded-sm shadow-sm border-amber-500/30" data-testid="lower-fsi-card">
          <CardHeader>
            <CardTitle className="font-heading tracking-tight">Use Lower FSI?</CardTitle>
            <CardDescription>
              {formData.projectClassification === "mhada" 
                ? "Optionally use lower FSI than the standard MHADA FSI of 3.0"
                : "Optionally use lower FSI than permissible (Paid FSI & TDR will be proportionally reduced)"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Do you want to use lower FSI?</Label>
                <RadioGroup 
                  value={formData.useLowerFSI || "no"} 
                  onValueChange={(value) => setFormData({...formData, useLowerFSI: value})}
                  data-testid="use-lower-fsi-radio"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="lower-fsi-no" data-testid="lower-fsi-no" />
                    <Label htmlFor="lower-fsi-no" className="font-normal cursor-pointer">
                      No (Use full {formData.projectClassification === "mhada" ? "MHADA FSI of 3.0" : "permissible FSI"})
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="lower-fsi-yes" data-testid="lower-fsi-yes" />
                    <Label htmlFor="lower-fsi-yes" className="font-normal cursor-pointer">Yes (Enter custom lower FSI)</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.useLowerFSI === "yes" && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <Label htmlFor="customLowerFSI">Enter Lower FSI Value</Label>
                  <Input
                    id="customLowerFSI"
                    name="customLowerFSI"
                    type="number"
                    step="0.1"
                    value={formData.customLowerFSI}
                    onChange={handleChange}
                    placeholder={`Between 1.1 and ${fsiBreakdown?.permissibleFSI || (formData.projectClassification === "mhada" ? 3.0 : 2.5)}`}
                    className="rounded-sm max-w-xs"
                    data-testid="custom-lower-fsi-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    <Info className="h-3 w-3 inline mr-1" />
                    Must be between Basic FSI (1.1) and {formData.projectClassification === "mhada" ? "MHADA FSI (3.0)" : `Permissible FSI (${fsiBreakdown?.permissibleFSI?.toFixed(2) || "N/A"})`}
                  </p>
                  {fsiBreakdown?.useLowerFSI && (
                    <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-sm text-sm">
                      <p><strong>Effective FSI:</strong> {fsiBreakdown.effectiveFSI?.toFixed(2)}</p>
                      {formData.projectClassification !== "mhada" && (
                        <p><strong>Reduction:</strong> {((1 - (fsiBreakdown.reductionRatio || 1)) * 100).toFixed(1)}% reduction in Paid FSI & TDR</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project Type & Residential/Commercial Mix */}
      <Card className="rounded-sm shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Project Type</CardTitle>
          <CardDescription>Ancillary FSI: 60% residential, 80% commercial</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-sm font-medium p-3 bg-secondary/50 rounded-sm">
              {formData.projectType === "residential" && "Residential Only — Ancillary FSI at 60% of total buildable area"}
              {formData.projectType === "commercial" && "Commercial Only — Ancillary FSI at 80% of total buildable area"}
              {formData.projectType === "mixed" && `Mixed Use — Residential: ${formData.residentialPercentage}% / Commercial: ${formData.commercialPercentage}%`}
            </div>
            <p className="text-xs text-muted-foreground">Change project type on Quick Input page</p>
          </div>
        </CardContent>
      </Card>

      {/* FSI Breakdown Display */}
      {fsiBreakdown && (
        <Card className="rounded-sm shadow-sm border-accent/50" data-testid="fsi-breakdown-card">
          <CardHeader>
            <CardTitle className="font-heading tracking-tight text-accent">FSI Calculation Breakdown</CardTitle>
            {fsiBreakdown.isMhada && (
              <p className="text-sm text-purple-600 font-medium mt-1">
                🏠 MHADA Project: Total FSI = 3.0 (Base 1.1 + MHADA 1.9). No TDR/Paid FSI applicable.
              </p>
            )}
            {fsiBreakdown.is6MeterRoad && (
              <p className="text-sm text-amber-600 font-medium mt-1">
                ⚠️ 6 Meter Road: Only Basic FSI (1.1) {fsiBreakdown.isOver30Years ? "+ Incentive FSI (0.3)" : ""} applies. No TDR/Paid FSI.
              </p>
            )}
            {fsiBreakdown.useLowerFSI && (
              <p className="text-sm text-amber-600 font-medium mt-1">
                📉 Using Lower FSI: {fsiBreakdown.effectiveFSI?.toFixed(2)} instead of {fsiBreakdown.permissibleFSI?.toFixed(2)}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Plot Areas (Sq Mts)</h4>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gross Plot Area</span>
                    <span className="font-mono font-medium">{fsiBreakdown.grossPlotArea.toFixed(2)} sq m</span>
                  </div>
                  {fsiBreakdown.newRoadWidening > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>New Road Widening (affects FSI)</span>
                      <span className="font-mono font-medium">-{fsiBreakdown.newRoadWidening.toFixed(2)} sq m</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Net Plot Area</span>
                    <span className="font-mono font-medium">{fsiBreakdown.netPlotArea.toFixed(2)} sq m</span>
                  </div>
                  {fsiBreakdown.oldRoadWidening > 0 && (
                    <div className="flex justify-between text-blue-600 mt-2 pt-2 border-t">
                      <span>Old Road Widening (TDR only)</span>
                      <span className="font-mono font-medium">{fsiBreakdown.oldRoadWidening.toFixed(2)} sq m</span>
                    </div>
                  )}
                  {fsiBreakdown.tdrReductionFromRoadWidening > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>TDR Reduction (Old×1 + New×2)</span>
                      <span className="font-mono font-medium">{fsiBreakdown.tdrReductionFromRoadWidening.toFixed(2)} sq m</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">FSI Components</h4>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Permissible FSI</span>
                    <span className="font-mono font-medium text-accent">{fsiBreakdown.permissibleFSI.toFixed(2)}</span>
                  </div>
                  {fsiBreakdown.useLowerFSI && (
                    <div className="flex justify-between">
                      <span className="text-amber-600">Effective FSI (Lower)</span>
                      <span className="font-mono font-medium text-amber-600">{fsiBreakdown.effectiveFSI?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Basic FSI</span>
                    <span className="font-mono font-medium">{fsiBreakdown.basicFSI.toFixed(2)}</span>
                  </div>
                  {fsiBreakdown.is6MeterRoad && fsiBreakdown.incentiveFSI > 0 && (
                    <div className="flex justify-between">
                      <span className="text-chart-1">Incentive FSI (30+ years)</span>
                      <span className="font-mono font-medium text-chart-1">{fsiBreakdown.incentiveFSI.toFixed(2)}</span>
                    </div>
                  )}
                  {fsiBreakdown.isMhada ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-purple-600 font-medium">MHADA FSI (Purchasable)</span>
                        <span className="font-mono font-medium text-purple-600">{fsiBreakdown.mhadaFSI?.toFixed(2) || '1.90'}</span>
                      </div>
                      {fsiBreakdown.mhadaIncentiveFSIArea > 0 && (
                        <div className="flex justify-between">
                          <span className="text-chart-1 font-medium">Incentive FSI (26.20 x {parseInt(formData.numberOfMembers) || 0})</span>
                          <span className="font-mono font-medium text-chart-1">{fsiBreakdown.mhadaIncentiveFSIArea?.toFixed(2)} sq m</span>
                        </div>
                      )}
                      {fsiBreakdown.titbitArea > 0 && (
                        <div className="flex justify-between">
                          <span className="text-amber-600 font-medium">Titbit Area</span>
                          <span className="font-mono font-medium text-amber-600">{fsiBreakdown.titbitArea?.toFixed(2)} sq m</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Paid FSI</span>
                        <span className="font-mono font-medium">{fsiBreakdown.paidFSI.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total TDR</span>
                        <span className="font-mono font-medium">{fsiBreakdown.totalTDR.toFixed(2)}</span>
                      </div>
                      {!fsiBreakdown.is6MeterRoad && fsiBreakdown.totalTDR > 0 && (
                        <>
                          <div className="flex justify-between pl-4">
                            <span className="text-muted-foreground text-xs">Amenity TDR</span>
                            <span className="font-mono text-xs">{fsiBreakdown.amenityTDR.toFixed(3)}</span>
                          </div>
                          <div className="flex justify-between pl-4">
                            <span className="text-muted-foreground text-xs">Slum TDR</span>
                            <span className="font-mono text-xs">{fsiBreakdown.slumTDR.toFixed(3)}</span>
                          </div>
                        </>
                      )}
                      {fsiBreakdown.isCbd && fsiBreakdown.cbdPremiumFSI > 0 && (
                        <div className="flex justify-between">
                          <span className="text-amber-700 font-medium">CBD Premium FSI</span>
                          <span className="font-mono font-medium text-amber-700">+{fsiBreakdown.cbdPremiumFSI.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Green FSI</span>
                    <span className="font-mono font-medium">{fsiBreakdown.greenFSI.toFixed(3)}</span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">FSI Areas (Sq Mts)</h4>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Basic FSI (1.1)</span>
                    <span className="font-mono">{fsiBreakdown.basicFSIArea.toFixed(2)} sq m</span>
                  </div>
                  {fsiBreakdown.is6MeterRoad && fsiBreakdown.incentiveFSIArea > 0 && (
                    <div className="flex justify-between">
                      <span className="text-chart-1">Incentive FSI (0.3)</span>
                      <span className="font-mono text-chart-1">{fsiBreakdown.incentiveFSIArea.toFixed(2)} sq m</span>
                    </div>
                  )}
                  {fsiBreakdown.isMhada ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-purple-600">MHADA FSI ({fsiBreakdown.mhadaFSI?.toFixed(2) || '1.90'})</span>
                        <span className="font-mono text-purple-600">{fsiBreakdown.mhadaFSIArea?.toFixed(2) || '0.00'} sq m</span>
                      </div>
                      {fsiBreakdown.mhadaIncentiveFSIArea > 0 && (
                        <div className="flex justify-between">
                          <span className="text-chart-1">Incentive FSI (26.20 x {parseInt(formData.numberOfMembers) || 0})</span>
                          <span className="font-mono text-chart-1">{fsiBreakdown.mhadaIncentiveFSIArea?.toFixed(2)} sq m</span>
                        </div>
                      )}
                      {fsiBreakdown.titbitArea > 0 && (
                        <div className="flex justify-between">
                          <span className="text-amber-600">Titbit Area (added to plot)</span>
                          <span className="font-mono text-amber-600">{fsiBreakdown.titbitArea?.toFixed(2)} sq m</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Paid FSI ({fsiBreakdown.paidFSI.toFixed(2)})</span>
                        <span className="font-mono">{fsiBreakdown.paidFSIArea.toFixed(2)} sq m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total TDR ({fsiBreakdown.totalTDR.toFixed(2)})</span>
                        <span className="font-mono">{fsiBreakdown.tdrArea.toFixed(2)} sq m</span>
                      </div>
                      {!fsiBreakdown.is6MeterRoad && fsiBreakdown.totalTDR > 0 && (
                        <>
                          <div className="flex justify-between pl-4 text-xs">
                            <span className="text-muted-foreground">• Amenity TDR ({((fsiBreakdown.amenityTDR / fsiBreakdown.totalTDR) * 100).toFixed(0)}%)</span>
                            <span className="font-mono">{(fsiBreakdown.amenityTDR * fsiBreakdown.grossPlotArea).toFixed(2)} sq m</span>
                          </div>
                          <div className="flex justify-between pl-4 text-xs">
                            <span className="text-muted-foreground">• Slum TDR ({((fsiBreakdown.slumTDR / fsiBreakdown.totalTDR) * 100).toFixed(0)}%)</span>
                            <span className="font-mono">{(fsiBreakdown.slumTDR * fsiBreakdown.grossPlotArea).toFixed(2)} sq m</span>
                          </div>
                        </>
                      )}
                      {fsiBreakdown.isCbd && fsiBreakdown.cbdPremiumFSI > 0 && (
                        <div className="flex justify-between bg-amber-100/50 -mx-2 px-2 py-0.5 rounded-sm">
                          <span className="text-amber-800 font-medium">CBD Premium FSI (+{fsiBreakdown.cbdPremiumFSI.toFixed(2)})</span>
                          <span className="font-mono text-amber-800 font-medium">{fsiBreakdown.cbdPremiumArea.toFixed(2)} sq m</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Green FSI ({fsiBreakdown.greenFSI.toFixed(3)})</span>
                    <span className="font-mono">{fsiBreakdown.greenFSIArea.toFixed(2)} sq m</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span>{fsiBreakdown.isMhada ? 'Subtotal (Basic + MHADA + Green)' : (fsiBreakdown.isCbd && fsiBreakdown.cbdPremiumFSI > 0 ? 'Subtotal (Basic + Paid + TDR + Green + CBD Premium)' : 'Subtotal (Basic + Paid + TDR + Green)')}</span>
                    <span className="font-mono text-accent">{fsiBreakdown.totalBuildableArea.toFixed(2)} sq m</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Ancillary FSI (Sq Mts)</h4>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Residential (60%)</span>
                    <span className="font-mono font-medium">{fsiBreakdown.ancillaryResidential.toFixed(2)} sq m</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Commercial (80%)</span>
                    <span className="font-mono font-medium">{fsiBreakdown.ancillaryCommercial.toFixed(2)} sq m</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Ancillary</span>
                    <span className="font-mono font-medium">{fsiBreakdown.totalAncillary.toFixed(2)} sq m</span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div className="bg-accent/10 p-4 rounded-sm border border-accent/30">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total Buildable (excl. ancillary) <span className="text-xs text-muted-foreground">({(fsiBreakdown.totalBuildableArea / fsiBreakdown.grossPlotArea).toFixed(2)} FSI)</span></span>
                  <span className="font-mono font-bold text-lg">{fsiBreakdown.totalBuildableArea.toFixed(2)} sq m</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-accent">Total with Ancillary FSI <span className="text-xs text-muted-foreground">({(fsiBreakdown.totalFSIWithAncillary / fsiBreakdown.grossPlotArea).toFixed(2)} FSI)</span></span>
                  <span className="font-mono font-bold text-lg text-accent">{fsiBreakdown.totalFSIWithAncillary.toFixed(2)} sq m</span>
                </div>
              </div>
            </div>
            
            <div className="text-xs text-muted-foreground bg-secondary/50 p-3 rounded-sm">
              <p className="font-medium mb-1">Calculation Note:</p>
              <p>• Road width: {formData.roadWidth} {formData.roadWidthUnit} {formData.roadWidthUnit === "feet" ? `(${(parseFloat(formData.roadWidth) * 0.3048).toFixed(2)} meters for FSI)` : ""}</p>
              <p>• Plot area: {formData.plotAreaPerCard} {formData.areaUnit === "sqft" ? "sq ft" : "sq mts"} {formData.areaUnit === "sqft" ? `(${(parseFloat(formData.plotAreaPerCard) * 0.092903).toFixed(2)} sq mts for FSI)` : ""}</p>
            </div>

            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={exportFSICalcPDF} data-testid="export-fsi-calc-pdf">
                <Download className="mr-2 h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FSICalculator;